import { HealthStatus } from '@provision/conversation-types';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Loader } from 'src/components';
import { useNavigationItems } from 'src/components/Navbar/use-navigation-items';
import { useDbHealthStatus } from 'src/graphql/entities/health-status/hooks';
import { HealthErrorPage } from 'src/pages/HealthErrorPage';
import { OriginalEmail } from 'src/pages/OriginalEmail';
import { trimDuplicatedSlashes } from 'src/utils/routing/use-navigate-route';
import { MainPanelContainer } from './components/main-panel-container';
import { ProtectedPage } from './components/protected-page';
import { RoutesContainer } from './components/routes-container';
import { useSyncPartnerUrlAccess } from './hooks';
import { CrmRoutes } from './pages/crm';
import { EntityRoutes } from './pages/entity';
import { InboxRoutes } from './pages/inbox';
import { LegacyRouteRedirects } from './pages/legacy-routes';
import { ReportsRoutes } from './pages/reports';
import { SearchRoutes } from './pages/search';
import { SettingsRoutes } from './pages/settings';
import { ViewsRoutes } from './pages/views';
import { getRoutePath } from './utils';

const LandingRedirect = ({ route }: { route?: string | null }) => {
  if (!route) {
    return;
  }

  return <Navigate to={route} replace />;
};

/**
 * Renders the common set of routes.
 * These routes are shared between the partner-specific and
 * non-partner versions of the app.
 */
const renderAppRoutes = ({
  landingRoute: rawLandingRoute,
  relative,
}: {
  landingRoute?: string | null;
  relative?: boolean;
}) => {
  const landingRoute = trimDuplicatedSlashes(`/${rawLandingRoute}`);
  return (
    <Route element={<ProtectedPage />}>
      <Route element={<RoutesContainer />}>
        <Route element={<MainPanelContainer />}>
          <Route path="*" element={<LandingRedirect route={landingRoute} />} />

          {LegacyRouteRedirects({ relative })}
          {SearchRoutes({ relative })}
          {ViewsRoutes({ relative })}
          {EntityRoutes({ relative })}
          {CrmRoutes({ relative })}
          {ReportsRoutes({ relative })}
          {SettingsRoutes({ relative })}
          {InboxRoutes({ relative })}
        </Route>
      </Route>

      {/* This page/view should not include the navigation bar */}
      <Route
        path={getRoutePath(
          portalPaths.originalEmail(UrlParams.conversationsMessageId),
          relative,
        )}
        element={<OriginalEmail />}
      />
    </Route>
  );
};

const AssertRouteHOCLayout = () => {
  const { isReady } = useSyncPartnerUrlAccess();
  const { status } = useDbHealthStatus();

  if (!isReady || !status) {
    return <Loader size={72} />;
  }

  if (status === HealthStatus.not_reachable) {
    return <HealthErrorPage />;
  }

  return <Outlet />;
};

export const AppRoutes = () => {
  const { landingRoute } = useNavigationItems();

  return (
    <Routes>
      <Route element={<AssertRouteHOCLayout />}>
        {/* Route with a partner identifier prefix */}
        <Route path={`${UrlParams.friendlyPartnerId}/*`}>
          {renderAppRoutes({ landingRoute, relative: true })}
        </Route>

        {/* Route without a partner identifier (multitenancy mode) */}
        <Route path="/*">{renderAppRoutes({ landingRoute, relative: true })}</Route>
      </Route>
    </Routes>
  );
};
